import React, { useCallback, useEffect, useRef } from 'react';
import { Box, IconButton, Paper, Stack } from '@mui/material';
import {
  FormatBold as FormatBoldIcon,
  FormatColorText as FormatColorTextIcon,
  FormatItalic as FormatItalicIcon,
  FormatListBulleted as FormatListBulletedIcon,
  FormatListNumbered as FormatListNumberedIcon,
  FormatUnderlined as FormatUnderlinedIcon,
  Link as LinkIcon,
} from '@mui/icons-material';

const RichTextEditor = ({ value, onChange, placeholder = 'Type content...' }) => {
  const editorRef = useRef(null);

  useEffect(() => {
    if (!editorRef.current) {
      return;
    }
    const currentHtml = editorRef.current.innerHTML;
    const nextHtml = value || '';
    if (currentHtml !== nextHtml) {
      editorRef.current.innerHTML = nextHtml;
    }
  }, [value]);

  const runCommand = useCallback(
    (command, commandValue = null) => {
      if (!editorRef.current) {
        return;
      }
      editorRef.current.focus();
      document.execCommand(command, false, commandValue);
      onChange(editorRef.current.innerHTML);
    },
    [onChange]
  );

  const applyLink = useCallback(() => {
    const url = window.prompt('Enter URL (https://...)');
    if (!url) {
      return;
    }
    runCommand('createLink', url.trim());
  }, [runCommand]);

  const handleInput = useCallback(() => {
    if (!editorRef.current) {
      return;
    }
    onChange(editorRef.current.innerHTML);
  }, [onChange]);

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <Stack
        direction="row"
        spacing={0.5}
        sx={{ p: 0.8, borderBottom: '1px solid rgba(15,23,42,0.12)', flexWrap: 'wrap' }}
      >
        <IconButton size="small" onClick={() => runCommand('bold')}>
          <FormatBoldIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={() => runCommand('italic')}>
          <FormatItalicIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={() => runCommand('underline')}>
          <FormatUnderlinedIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={() => runCommand('insertUnorderedList')}>
          <FormatListBulletedIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={() => runCommand('insertOrderedList')}>
          <FormatListNumberedIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={applyLink}>
          <LinkIcon fontSize="small" />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 0.4 }}>
          <FormatColorTextIcon fontSize="small" />
          <input
            type="color"
            defaultValue="#1f2937"
            onChange={(event) => runCommand('foreColor', event.target.value)}
            style={{ width: 26, height: 24, border: 0, background: 'transparent', cursor: 'pointer' }}
            aria-label="Text color"
          />
        </Box>
      </Stack>

      <Box
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        sx={{
          minHeight: 130,
          p: 1.2,
          fontSize: '0.92rem',
          lineHeight: 1.68,
          color: 'text.primary',
          outline: 'none',
          '&:empty:before': {
            content: `"${placeholder}"`,
            color: 'text.disabled',
          },
        }}
      />
    </Paper>
  );
};

export default RichTextEditor;
